import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  arrayUnion 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAllPayments } from './payment';
import dataCache from '../utils/cache';

// Get all education levels
export const getLevels = async () => {
  try {
    const cacheKey = 'levels_all';
    
    // Check cache first
    const cachedLevels = dataCache.get(cacheKey);
    if (cachedLevels) {
      console.log('📦 Returning cached levels');
      return cachedLevels;
    }
    
    console.log('🔄 Fetching fresh levels');
    
    const levelsRef = collection(db, 'levels');
    const q = query(levelsRef, where('isActive', '==', true));
    
    const querySnapshot = await getDocs(q);
    let levels = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by sortOrder
    levels.sort((a, b) => {
      if (a.sortOrder && b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return a.name.localeCompare(b.name);
    });
    
    // Cache the result
    dataCache.set(cacheKey, levels, 300000); // 5 minutes
    
    return levels;
  } catch (error) {
    console.error('Error fetching levels:', error);
    return [];
  }
};

// Get subjects for user's level
export const getSubjects = async (levelId) => {
  try {
    const cacheKey = `subjects_${levelId}`;
    
    // Check cache first
    const cachedSubjects = dataCache.get(cacheKey);
    if (cachedSubjects) {
      console.log('📦 Returning cached subjects for level:', levelId);
      return cachedSubjects;
    }
    
    console.log('🔄 Fetching fresh subjects for level:', levelId);
    
    const subjectsRef = collection(db, 'subjects');
    
    // Use simpler query that doesn't require composite index
    const q = query(
      subjectsRef,
      where('levelId', '==', levelId),
      where('isActive', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    let subjects = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Sort by sortOrder in JavaScript (fallback to name if no sortOrder)
    subjects.sort((a, b) => {
      if (a.sortOrder && b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return (a.name || '').localeCompare(b.name || '');
    });
    
    // Cache the results for 10 minutes
    dataCache.set(cacheKey, subjects, 10 * 60 * 1000);
    
    console.log('✅ Cached subjects for level:', levelId);
    return subjects;
  } catch (error) {
    console.error('Error fetching subjects:', error);
    throw error;
  }
};

// Get past papers for user's level and subject
export const getPapers = async (levelId, subjectId = null) => {
  try {
    // Handle invalid levelId
    if (!levelId || levelId === 'all') {
      console.log('⚠️ Invalid levelId provided to getPapers:', levelId);
      return [];
    }
    
    const cacheKey = `papers_${levelId}_${subjectId || 'all'}`;
    
    // Check cache first
    const cachedPapers = dataCache.get(cacheKey);
    if (cachedPapers) {
      console.log('📦 Returning cached papers for level:', levelId, 'subject:', subjectId);
      return cachedPapers;
    }
    
    console.log('🔄 Fetching fresh papers for level:', levelId, 'subject:', subjectId);
    
    // Use simplest query to avoid composite index requirement
    const papersRef = collection(db, 'papers');
    let q = query(
      papersRef,
      where('levelId', '==', levelId)
    );
    
    console.log('📝 Papers query structure (simplified):', q);
    
    const querySnapshot = await getDocs(q);
    let papers = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by isActive and subjectId in JavaScript to avoid composite index
    papers = papers.filter(paper => paper.isActive === true);
    if (subjectId) {
      papers = papers.filter(paper => paper.subjectId === subjectId);
    }
    
    // Sort by year and term in JavaScript to avoid composite index requirement
    papers.sort((a, b) => {
      // First sort by year (descending)
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      // Then sort by term (1, 2, 3 order)
      const termOrder = { '1': 1, '2': 2, '3': 3 };
      const termA = termOrder[a.term] || 0;
      const termB = termOrder[b.term] || 0;
      return termA - termB;
    });
    
    // Cache the results for 15 minutes
    dataCache.set(cacheKey, papers, 15 * 60 * 1000);
    
    console.log('✅ Cached papers for level:', levelId, 'subject:', subjectId);
    return papers;
  } catch (error) {
    console.error('Error fetching papers:', error);
    throw error;
  }
};

// Get videos for user's level and subject
export const getVideos = async (levelId, subjectId = null) => {
  try {
    const cacheKey = `videos_${levelId}_${subjectId || 'all'}`;
    
    // Check cache first
    const cachedVideos = dataCache.get(cacheKey);
    if (cachedVideos) {
      console.log('📦 Returning cached videos for level:', levelId, 'subject:', subjectId);
      return cachedVideos;
    }
    
    console.log('🔄 Fetching fresh videos for level:', levelId, 'subject:', subjectId);
    
    const videosRef = collection(db, 'videos');
    let q = query(
      videosRef,
      where('levelId', '==', levelId),
      where('isActive', '==', true),
      orderBy('order', 'asc')
    );
    
    if (subjectId) {
      q = query(q, where('subjectId', '==', subjectId));
    }
    
    const querySnapshot = await getDocs(q);
    const videos = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Cache the results for 20 minutes
    dataCache.set(cacheKey, videos, 20 * 60 * 1000);
    
    console.log('✅ Cached videos for level:', levelId, 'subject:', subjectId);
    return videos;
  } catch (error) {
    console.error('Error fetching videos:', error);
    throw error;
  }
};

// Get exams for user's level and subject
export const getExams = async (levelId, subjectId = null) => {
  try {
    const cacheKey = `exams_${levelId}_${subjectId || 'all'}`;
    
    // Check cache first
    const cachedExams = dataCache.get(cacheKey);
    if (cachedExams) {
      console.log('📦 Returning cached exams for level:', levelId, 'subject:', subjectId);
      return cachedExams;
    }
    
    console.log('🔄 Fetching fresh exams for level:', levelId, 'subject:', subjectId);
    
    const examsRef = collection(db, 'exams');
    let q = query(
      examsRef,
      where('levelId', '==', levelId),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    if (subjectId) {
      q = query(q, where('subjectId', '==', subjectId));
    }
    
    const querySnapshot = await getDocs(q);
    const exams = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Cache the results for 5 minutes (exams might change more frequently)
    dataCache.set(cacheKey, exams, 5 * 60 * 1000);
    
    console.log('✅ Cached exams for level:', levelId, 'subject:', subjectId);
    return exams;
  } catch (error) {
    console.error('Error fetching exams:', error);
    throw error;
  }
};

// Get single document by ID
export const getDocument = async (collectionName, docId) => {
  try {
    const docRef = doc(db, collectionName, docId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } else {
      throw new Error('Document not found');
    }
  } catch (error) {
    console.error('Error fetching document:', error);
    throw error;
  }
};

// Increment paper download count
export const incrementPaperDownload = async (paperId) => {
  try {
    const paperRef = doc(db, 'papers', paperId);
    await updateDoc(paperRef, {
      downloadCount: arrayUnion(1)
    });
  } catch (error) {
    console.error('Error incrementing download count:', error);
    throw error;
  }
};

// Get user's exam results
export const getExamResults = async (userId, levelId) => {
  try {
    console.log('🔄 NEW getExamResults function loaded!', new Date().toISOString());
    console.log('🔍 Query params:', { userId, levelId });
    
    // Use simplest query to avoid composite index requirement
    const resultsRef = collection(db, 'exam_results');
    let q = query(
      resultsRef,
      where('userId', '==', userId)
    );
    
    console.log('📝 Query structure (simplified):', q);
    
    const querySnapshot = await getDocs(q);
    let results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('📊 Raw results count:', results.length);
    
    // Filter by levelId and sort in JavaScript to avoid composite index
    results = results
      .filter(result => result.levelId === levelId)
      .sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA; // Descending order
      })
      .slice(0, 10); // Limit to 10 results
    
    console.log('✅ Filtered results count:', results.length);
    return results;
  } catch (error) {
    console.error('Error fetching exam results:', error);
    throw error;
  }
};

// Get recent activity for dashboard
export const getRecentActivity = async (userId, levelId) => {
  try {
    const [recentResults, recentPapers] = await Promise.all([
      getExamResults(userId, levelId).then(results => results.slice(0, 5)),
      getPapers(levelId).then(papers => papers.slice(0, 5))
    ]);
    
    return {
      recentResults,
      recentPapers
    };
  } catch (error) {
    console.error('Error fetching recent activity:', error);
    throw error;
  }
};

// Get detailed exam results for performance overview
export const getDetailedExamResults = async (userId, levelId) => {
  try {
    console.log('🔍 Fetching detailed exam results for:', { userId, levelId });
    
    // Use simplest query to avoid composite index requirement
    const resultsRef = collection(db, 'exam_results');
    let q = query(
      resultsRef,
      where('userId', '==', userId)
    );
    
    console.log('📝 Query constructed (simplified):', q);
    
    const querySnapshot = await getDocs(q);
    let results = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Filter by levelId and sort in JavaScript to avoid composite index
    results = results
      .filter(result => result.levelId === levelId)
      .sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA; // Descending order
      })
      .slice(0, 20); // Limit to 20 results
    
    console.log('📊 Query results:', results.length, 'documents found');
    console.log('📋 Results data:', results);
    
    return results;
  } catch (error) {
    console.error('Error fetching detailed exam results:', error);
    throw error;
  }
};

// Get payment analytics
export const getPaymentAnalytics = async (startDate = null, endDate = null) => {
  const analytics = await getAllPayments();
  
  const filteredAnalytics = analytics.filter(payment => {
    const paymentDate = payment.createdAt.toDate();
    if (startDate && paymentDate < new Date(startDate)) return false;
    if (endDate && paymentDate > new Date(endDate)) return false;
    return true;
  });

  return {
    totalPayments: filteredAnalytics.length,
    totalRevenue: filteredAnalytics.reduce((sum, payment) => sum + payment.amount, 0),
    pendingPayments: filteredAnalytics.filter(p => p.status === 'pending').length,
    approvedPayments: filteredAnalytics.filter(p => p.status === 'approved').length,
    rejectedPayments: filteredAnalytics.filter(p => p.status === 'rejected').length
  };
};

const contentServices = {
  getSubjects,
  getPapers,
  getVideos,
  getExams,
  getDocument,
  incrementPaperDownload,
  getExamResults,
  getRecentActivity,
  getPaymentAnalytics
};

export default contentServices;
